import React, { useEffect, useState } from 'react'
import cloneDeep from 'lodash.clonedeep'
import { useQuery, useQueryClient } from 'react-query'
import { useRouter } from 'next/router'

import { useModal } from '@hooks/contexts/ModalContext/ModalContext'
import { usePagination } from '@hooks/usePagination'
import { useAuthentication } from '@hooks/useAuthentication'
import { useAlert } from '@hooks/useAlert'
import { SupportCharacterFormType } from '@components/SupportCharacterModal/interface'
import { AllCharacterType } from '@models/character/AllCharacterTyp'
import { AllCharactersResponse } from '@models/character/AllCharactersResponse'
import { BookEnum } from '@interfaces/BookEnum'
import { scrollToElement } from '@lib/utils'
import { useCharacterAction } from '@hooks/character/useCharacterAction'
import { CharacterListProps, WithCharacterListProps } from './interface'
import { OptionFormType } from '../OptionForm/interface'

const withCharacterList = (Component: React.FC<CharacterListProps>) => {
  const WithCharacterList = ({
    text,
    orderBy,
    roleId,
    chapterType,
    advanceSearch,
  }: WithCharacterListProps) => {
    const router = useRouter()
    const [searchParams, setSearchParams] = useState<OptionFormType>({
      searchText: text,
      characterOrder: orderBy,
      characterRoleId: roleId,
      chapterType,
      categoryIds: [],
      bookTypes: [],
    })
    const { page, perpage, pageChange, perpageChange } = usePagination({
      initialPerpage: 20,
    })
    const { user, userCoin, isAuthenticated, isUserLoading } =
      useAuthentication()
    const characterClient = useCharacterAction()
    const queryClient = useQueryClient()
    const alert = useAlert()
    const supportCharacterModal = useModal({ modal: 'supportCharacter' }, true)
    const loginModal = useModal({ modal: 'login' })
    const notEnoughCoinModal = useModal({
      modal: 'notEnoughCoin',
      modalProps: {
        describeText: '',
        purposeText: 'สนับสนุนตัวละคร',
      },
    })
    const changeEmailModal = useModal({ modal: 'changeEmail' }, true)

    const { data: allCharacters, isLoading } = useQuery(
      ['all-characters', searchParams, page, perpage],
      () =>
        characterClient.getAllCharacters({
          ...searchParams,
          page,
          limitPerPage: perpage,
        })
    )

    async function handleSearch(form: OptionFormType) {
      pageChange(1)

      const query: any = {
        text: form.searchText,
        roleId: form.characterRoleId,
        advanceSearch,
      }
      if (form.bookTypes.length) query.bookTypes = form.bookTypes
      if (form.categoryIds.length) query.categoryIds = form.categoryIds
      if (form.characterOrder) query.orderBy = form.characterOrder
      if (form.chapterType) query.chapterType = form.chapterType

      router.replace(
        {
          pathname: router.pathname,
          query,
        },
        undefined,
        { scroll: false }
      )
    }

    function handlePerPageChange(newPerPage: number) {
      pageChange(1)
      perpageChange(newPerPage)
    }

    function handlePageChange(newPage: number) {
      pageChange(newPage)
      scrollToElement('#character-list')
    }

    function supportCharacter(character: AllCharacterType) {
      if (
        (userCoin.freeCoin && userCoin.freeCoin.value >= 1) ||
        (userCoin.paidCoin && userCoin.paidCoin.value >= 1) ||
        (userCoin.thirdCoin && userCoin.thirdCoin.value >= 1)
      ) {
        supportCharacterModal.show({
          title: character.book.title,
          name: character.name,
          roleName: character.characterRole?.name,
          imgPath: character.imgPath,
          onSubmit: async ({
            freeCoin,
            paidCoin,
            thirdCoin,
          }: SupportCharacterFormType) => {
            try {
              const newData = await characterClient.createCharacterDonation(
                character.id,
                userCoin,
                freeCoin,
                paidCoin,
                thirdCoin
              )
              if (newData) {
                characterClient.triggerDonationCharacter(
                  {
                    freeCoin,
                    paidCoin,
                    thirdCoin,
                  },
                  newData.transactionId,
                  {
                    name: character.name,
                    id: character.id,
                  }
                )
                queryClient.setQueryData<AllCharactersResponse | undefined>(
                  ['all-characters', searchParams, page, perpage],
                  (oldData?: AllCharactersResponse) => {
                    if (oldData) {
                      const dataClone = cloneDeep(oldData)
                      const index = dataClone.data.findIndex(
                        item => item.id === newData.characterDonation.id
                      )
                      dataClone.data[index] = {
                        ...dataClone.data[index],
                        coinsTotal: newData.characterDonation.coinsTotal,
                        coinsToNextLevel:
                          newData.characterDonation.coinsToNextLevel,
                        characterRank: newData.characterDonation.characterRank,
                        characterNextRank:
                          newData.characterDonation.characterNextRank,
                      }

                      return dataClone
                    }
                    return oldData
                  }
                )
                queryClient.invalidateQueries('userCoin')
                alert.success(
                  `คุณได้สนับสนุนตัวละคร ${character.name} เรียบร้อยแล้ว`
                )
              }
            } catch (_) {
              alert.error(
                'เกิดข้อผิดพลาด สนับสนุนตัวละครไม่สำเร็จ กรุณาลองอีกครั้ง'
              )
            } finally {
              supportCharacterModal.hide()
            }
          },
          onCancel: () => {
            supportCharacterModal.hide()
          },
        })
      } else {
        notEnoughCoinModal.show()
      }
    }

    function handleSupportCharacter(character: AllCharacterType) {
      if (isAuthenticated) {
        if (user.emailEditable) {
          changeEmailModal.show({
            onSuccess: () => {
              supportCharacter(character)
            },
          })
        } else {
          supportCharacter(character)
        }
      } else {
        loginModal.show()
      }
    }

    useEffect(() => {
      const bookTypes = router.query.bookTypes
        ? typeof router.query.bookTypes === 'string'
          ? ([router.query.bookTypes] as BookEnum[])
          : (router.query.bookTypes as BookEnum[])
        : []
      const categoryIds = router.query.categoryIds
        ? typeof router.query.categoryIds === 'string'
          ? [router.query.categoryIds]
          : router.query.categoryIds
        : []

      setSearchParams({
        searchText: text,
        characterOrder: orderBy,
        characterRoleId: roleId,
        chapterType,
        categoryIds,
        bookTypes,
      })
    }, [
      text,
      orderBy,
      roleId,
      chapterType,
      router.query.bookTypes,
      router.query.categoryIds,
    ])

    const componentProps = {
      initialValues: searchParams,
      perpage,
      page,
      total: allCharacters?.total || 0,
      isLoading: isLoading || isUserLoading,
      characterList: allCharacters?.data ?? [],
      handleSupportCharacter,
      handleSearch,
      perpageChange: handlePerPageChange,
      handlePageChange,
    }

    return <Component {...componentProps} />
  }

  return WithCharacterList
}

export { withCharacterList }
